import axios from "axios";

import { endpoints } from "../../../api/endpoints";
import { SentimentResult } from "../types/interviewSession";
import { resolveIntakeSpeechLanguageCode } from "./intakeLocale";

const SPEECH_KEY = process.env.REACT_APP_GOOGLE_SPEECH_API_KEY ?? "";
const NLP_KEY = process.env.REACT_APP_GOOGLE_NLP_API_KEY ?? "";
const VISION_KEY = process.env.REACT_APP_GOOGLE_VISION_API_KEY ?? "";

async function proxyPost<T>(url: string, body: unknown): Promise<T | null> {
  try {
    const response = await axios.post<T>(url, body, { withCredentials: false });
    return response.data;
  } catch (error) {
    console.warn("[Intake] Backend proxy failed, trying direct API:", url, error);
    return null;
  }
}

async function directGooglePost<T>(url: string, body: unknown): Promise<T> {
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "omit",
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw Object.assign(new Error(`Google API ${response.status}`), {
      status: response.status,
      data: errorBody,
    });
  }
  return response.json() as Promise<T>;
}

function logApiFailure(label: string, error: unknown): void {
  console.error(`[Intake] ${label} failed:`, error);
}

type SpeechConfig = {
  encoding: string;
  languageCode: string;
  sampleRateHertz?: number;
  enableAutomaticPunctuation?: boolean;
};

function buildSpeechConfigs(mimeType?: string): SpeechConfig[] {
  const languageCode = resolveIntakeSpeechLanguageCode();
  const isWebm = !mimeType || mimeType.includes("webm");
  const configs: SpeechConfig[] = [];
  if (isWebm) {
    configs.push(
      { encoding: "WEBM_OPUS", languageCode, enableAutomaticPunctuation: true },
      { encoding: "WEBM_OPUS", sampleRateHertz: 48000, languageCode, enableAutomaticPunctuation: true }
    );
  }
  configs.push({ encoding: "OGG_OPUS", languageCode, enableAutomaticPunctuation: true });
  return configs;
}

function extractTranscript(data: unknown): string {
  const results = (data as { results?: Array<{ alternatives?: Array<{ transcript?: string }> }> })
    ?.results;
  if (!results?.length) return "";
  return results
    .map((r) => r.alternatives?.[0]?.transcript?.trim() ?? "")
    .filter(Boolean)
    .join(" ")
    .trim();
}

export async function speechToText(
  base64Audio: string,
  mimeType?: string
): Promise<string> {
  if (!base64Audio.trim()) return "";

  const proxy = await proxyPost<{ transcript?: string }>(endpoints.intakeSpeech, {
    audio: base64Audio,
    mimeType,
    languageCode: resolveIntakeSpeechLanguageCode(),
  });
  if (proxy?.transcript) return proxy.transcript;

  if (!SPEECH_KEY) {
    console.error("[Intake] Speech API unavailable (no proxy, no key)");
    return "";
  }

  for (const config of buildSpeechConfigs(mimeType)) {
    try {
      const data = await directGooglePost<{ results?: unknown[] }>(
        `https://speech.googleapis.com/v1/speech:recognize?key=${SPEECH_KEY}`,
        { config, audio: { content: base64Audio } }
      );
      const transcript = extractTranscript(data);
      if (transcript) return transcript;
    } catch (error) {
      logApiFailure(`Speech-to-Text (${config.encoding})`, error);
    }
  }
  return "";
}

export async function analyzeSentiment(text: string): Promise<SentimentResult> {
  const trimmed = text.trim();
  if (!trimmed) return { score: 0, magnitude: 0 };

  const proxy = await proxyPost<{ score?: number; magnitude?: number }>(
    endpoints.intakeSentiment,
    { text: trimmed }
  );
  if (proxy && typeof proxy.score === "number") {
    return { score: proxy.score, magnitude: proxy.magnitude ?? 0 };
  }

  if (!NLP_KEY) return { score: 0, magnitude: 0 };

  try {
    const data = await directGooglePost<{ documentSentiment?: { score?: number; magnitude?: number } }>(
      `https://language.googleapis.com/v1/documents:analyzeSentiment?key=${NLP_KEY}`,
      { document: { type: "PLAIN_TEXT", content: trimmed } }
    );
    return {
      score: data.documentSentiment?.score ?? 0,
      magnitude: data.documentSentiment?.magnitude ?? 0,
    };
  } catch (error) {
    logApiFailure("Natural Language sentiment", error);
    return { score: 0, magnitude: 0 };
  }
}

export type FaceEmotions = {
  joy: number;
  sorrow: number;
  anger: number;
};

export type FaceDetectionResult = {
  facesDetected: number;
  annotations: unknown[];
  emotions: FaceEmotions | null;
};

export async function detectFaces(base64Image: string): Promise<FaceDetectionResult> {
  if (!base64Image.trim()) {
    return { facesDetected: 0, annotations: [], emotions: null };
  }

  const proxy = await proxyPost<{
    facesDetected?: number;
    emotions?: FaceEmotions | null;
  }>(endpoints.intakeVision, { image: base64Image });

  if (proxy) {
    return {
      facesDetected: proxy.facesDetected ?? 0,
      annotations: [],
      emotions: proxy.emotions ?? null,
    };
  }

  if (!VISION_KEY) {
    return { facesDetected: 0, annotations: [], emotions: null };
  }

  try {
    const data = await directGooglePost<{ responses?: Array<{ faceAnnotations?: unknown[] }> }>(
      `https://vision.googleapis.com/v1/images:annotate?key=${VISION_KEY}`,
      {
        requests: [{ image: { content: base64Image }, features: [{ type: "FACE_DETECTION" }] }],
      }
    );
    const annotations = data.responses?.[0]?.faceAnnotations ?? [];
    return { facesDetected: annotations.length, annotations, emotions: null };
  } catch (error) {
    logApiFailure("Vision FACE_DETECTION", error);
    return { facesDetected: 0, annotations: [], emotions: null };
  }
}

export function captureVideoFrameBase64(
  video: HTMLVideoElement,
  quality = 0.85
): string {
  if (video.videoWidth === 0 || video.videoHeight === 0) return "";
  const canvas = document.createElement("canvas");
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) return "";
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/jpeg", quality).split(",")[1] ?? "";
}
